import React, { ComponentType, CSSProperties, ReactNode } from 'react';
import TextLink from '../../../../ui/TextLink/TextLink';
import Button from '../../../../ui/Button/BlockButton';
import PanelContentHeader from '../../../../ui/PanelContentHeader/PanelContentHeader';
import BitIconSpinner from '../../../../ui/Spinner/Spinner';
import { useTheme } from '../../../../../hooks/useTheme';
import styles from './ControlView.module.css';

interface ControlViewButtonAction {
  text: string;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
}

interface ControlViewProps {
  channel: string;
  isLandscape: boolean;
  featureName?: string;
  icon?: ReactNode;
  heading?: string;
  text?: string;
  primaryButton?: ControlViewButtonAction;
  secondaryButton?: ControlViewButtonAction & { link?: string };
}

const ControlView = ({
  channel,
  isLandscape,
  featureName,
  icon,
  heading,
  text,
  primaryButton,
  secondaryButton,
}: ControlViewProps) => {
  const {
    linksNoneDefault: unsubscribeLinkStyle,
    linksNoneHover: unsubscribeLinkHoverStyle,
  } = useTheme();

  return (
    <div
      className={styles.controlView}
      data-test={`${channel}NotificationCenterSubscribed`}
    >
      <div
        data-testid={`column-one-${isLandscape ? '7' : '12'}`}
        style={{ gridColumn: `span ${isLandscape ? '7' : '12'}` }}
      >
        <div
          className={
            isLandscape ? styles.panelContainerLandscape : styles.panelContainer
          }
        >
          <PanelContentHeader
            as="h2"
            icon={icon}
            headerText={heading || ''}
            headerTextRole="status"
            featureName={featureName}
            paragraphChildren={
              <>
                {text}
                {secondaryButton && isLandscape && (
                  <span className={styles.unsubscribeWrapperLandscape}>
                    {secondaryButton.loading ? (
                      <BitIconSpinner />
                    ) : (
                      <TextLink
                        linkStyle={{
                          sDefault: unsubscribeLinkStyle,
                          sHover: unsubscribeLinkHoverStyle,
                        }}
                        onClick={secondaryButton.onClick}
                      >
                        {secondaryButton.text}
                      </TextLink>
                    )}
                  </span>
                )}
              </>
            }
            isLandscape={isLandscape}
          />
        </div>
      </div>
      <div
        data-testid={`column-two-${isLandscape ? '5' : '12'}`}
        style={{ gridColumn: `span ${isLandscape ? '5' : '12'}` }}
      >
        {primaryButton && (
          <div
            className={
              isLandscape
                ? styles.buttonContainerLandscape
                : styles.buttonContainer
            }
            data-test={`${channel}UpdateInfoButtonContainer`}
          >
            <Button
              onClick={primaryButton.onClick}
              big={!isLandscape}
              disabled={primaryButton.disabled}
            >
              {primaryButton.text}
            </Button>
          </div>
        )}
        {secondaryButton && !isLandscape && (
          <div
            className={styles.unsubscribeWrapper}
            data-test={`${channel}UnsubscribeLinkContainerPortrait`}
          >
            <div className={styles.unsubscribeWrapper}>
              {secondaryButton.loading ? (
                <BitIconSpinner />
              ) : (
                <TextLink
                  linkStyle={{
                    sDefault: unsubscribeLinkStyle,
                    sHover: unsubscribeLinkHoverStyle,
                  }}
                  href={secondaryButton.link}
                  onClick={secondaryButton.onClick}
                >
                  {secondaryButton.text}
                </TextLink>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ControlView;
